import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaInstagram, FaTiktok, FaGoodreads, FaAmazon } from "react-icons/fa";
import Newsletter from '../../newsletter/Newsletter';
import Footer from '../../privacy-policy/Footer';

const Parallax = styled.div`
  background-image: url("/images/parallax.jpg");
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 10px;
`;

const RoundedBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  margin: clamp(15px, 5vw, 30px);
  margin-bottom: 0;
  padding: clamp(15px, 5vw, 60px);
  border-radius: clamp(15px, 3vw, 20px);
`;

const BookContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: clamp(10px, 5vw, 60px);

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const CoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CoverImage = styled.img`
  width: 100%;
  height: auto;
  padding-bottom: 10px;

  /* Large desktops & laptops */
  max-width: 400px;

  /* Tablets landscape / small laptops */
  @media (max-width: 1200px) {
    max-width: 350px;
  }

  /* Tablets portrait */
  @media (max-width: 992px) {
    max-width: 300px;
  }

  /* Large phones */
  @media (max-width: 768px) {
    max-width: 250px;
  }

  /* Small phones */
  @media (max-width: 480px) {
    max-width: 200px;
  }
`;

const ArcInfo = styled.div`
  display: inline-flex; 
  flex-direction: column; 
  align-items: center;
  white-space: nowrap;   
  max-width: 250px;     
`;

const Button = styled.button`
  background-color: #caaeecff;
  color: white;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  font-weight: bold;
  align-self: center;
  margin-top: 15px;
  padding: 10px;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #ba9debbb;
  }
`;

const DescriptionContainer = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  color: #0360ffa2;
  text-align: center;
  font-family: 'MedievalSharp', serif;
  font-size: clamp(1.5rem, 5vw, 2.8rem);
  padding-bottom: clamp(15px, 3vw, 25px);
`;

const SpecialText = styled.p`
  font-weight: bold;
  font-style: italic;
`;

const Paragraph = styled.p`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
`;

const PromoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 60px;
  padding: clamp(30px, 5vw, 60px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const BuyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ItalicText = styled.span`
  font-style: italic;
`;

const TropesContainer = styled.div`
  white-space: nowrap;   

  h1 {
    color: white;
    font-family: 'Londrina Sketch', serif;
    font-size: clamp(1.5rem, 5vw, 2rem);
    text-align: center;
  }
`;

const FullBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  padding: clamp(15px, 5vw, 60px);
`;

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(5px, 3vw, 20px);
`;

const QuoteBox = styled.div`
  padding: clamp(15px, 3vw, 25px);
  text-align: center;
`;

const Arrow = styled.button`
  height: fit-content;
  background: none;
  border: none;
  font-size: clamp(2rem, 2.5vw + 1rem, 4rem);
  font-weight: bold;
  color: #3695f4ff;
  cursor: pointer;

  &:hover {
    color: #0360ffa2;
    transform: scale(1.08);
  }
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin: clamp(15px, 5vw, 30px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const Link = styled.a`
  color: white;

  svg {
    width: clamp(40px, 5vw, 60px);
    height: auto;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.66);
    transform: scale(1.09);
  }
`;

const BeyondPrejudice = () => {
  useEffect(() => {
    document.title = "Beyond Prejudice - Anna Miki";
  }, []);

  const navigate = useNavigate();

  const quotes = [
    {
      text: 'Ana Miki’s Beyond Prejudice brings a fresh and exciting twist, blending romance, danger, and the complexities of overcoming preconceived notions. When Irina and Alex met, they have an instant connection but it’s more than young love. This issue between them is tearing both of their families apart. The reader will root for this couple throughout, but just when you think they’ve overcome the worst, their love and even their lives are in danger.',
      author: 'T.A. Caldwell'
    },
    {
      text: 'Beautiful prose, gripping plot, and a sweet young romance swept me into Anna Miki’s debut right from the first chapter. Dark family secrets, a haunting past, and a love that transcends boundaries kept me turning pages, wanting—needing—to know more and rooting for this couple that should have, by any rights, been enemies. Throw in a gorgeous setting in the mountains of Romania, plus a little music, and you’ve got the perfect mix!',
      author: 'Alex'
    },
    {
      text: 'This novel is a beautiful story of star-crossed lovers, and it\'s beyond dreamy. The plot moves at a thrilling pace, the romance comes together beautifully, but Irina and Alex are simply fantastic characters you\'ll root for. Their arcs (especially Alex\'s) are full of healing, growth, and finding support after tragedy. Make yourself a cup of cocoa, dream of the Carpathian Mountains, and settle in for this story because you\'re not going to want to put it down.',
      author: 'Sarah'
    },
    {
      text: 'Ana Miki’s writing captures you with vivid descriptions from the beginning. The forbidden romance that develops between Alex and Irina. From their first meeting, they have a spark that is so akin to young love. As the secrets unravel between them, you can’t help but to keep turning the page. Definitely a new twist on the star-crossed lovers trope.',
      author: 'Katie'
    }
  ];

  const [currentQuote, setCurrentQuote] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % quotes.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [quotes.length]);

  const handleNext = () => {
    setCurrentQuote((currentQuote + 1) % quotes.length);
  };

  const handlePrev = () => {
    setCurrentQuote((currentQuote - 1 + quotes.length) % quotes.length);
  };

  return (
    <Parallax>

      <RoundedBox>

        <BookContainer>
          
          <CoverContainer>

            <CoverImage src="/images/cover_b1.svg" alt="Beyond Prejudice by Anna Miki" />
            
            <ArcInfo>

              <Paragraph><strong>Release:</strong> 16 Nov, 2024</Paragraph>
              <Paragraph><strong>ARC Status:</strong> Closed</Paragraph>

            </ArcInfo>
            
            <Button onClick={() => navigate('/beyond-prejudice-sample')}>
              Read chapter 1
            </Button>

          </CoverContainer>

          <DescriptionContainer>

            <Title>Beyond Prejudice: A forbidden love story</Title>

            <Paragraph>
              <SpecialText>One brother holds her heart. The other held a knife to her throat—and he’s not finished yet.</SpecialText>
            </Paragraph> <br/>

            <Paragraph>
              After a violent home robbery shatters her life, Irina is haunted by crippling nightmares. A romantic relationship is the last thing on her mind, but with calming presence, witty humor, and the ultimate 90s rock playlist, Alex becomes her unexpected refuge—and her first kiss.
            </Paragraph> <br/>

            <Paragraph>
              Except his older brother is the very man from Irina's nightmares—and still on the run from the police. Their families are meant to sit on opposite sides of a courtroom, not around a dining room table.
            </Paragraph> <br/>

            <Paragraph>
              Caught in a forbidden love that reveals family secrets and pits brother against brother, Irina and Alex navigate a path of difficult choices as their love—and ultimately their lives—are on the line.
            </Paragraph> <br/>

            <Paragraph>
              Dive into a world where loyalty is tested and romance dares to bloom in the shadows.
            </Paragraph>

          </DescriptionContainer>

        </BookContainer>

      </RoundedBox>

      <PromoContainer>

        <BuyContainer>

          <Title style={{color: "rgba(255, 255, 255, 0.66)"}}>Buy the book</Title>

          <Paragraph style={{color: "white", textAlign: "center"}}>
            Buy <ItalicText>Beyond Prejudice</ItalicText> if you crave emotional rollercoasters, edge-of-the-seat suspense, family drama, and romance that lingers long after the last page.
          </Paragraph>

          <a 
            href="https://www.amazon.com/Beyond-Prejudice-forbidden-love-story-ebook/dp/B0DJZ9WG8W?ref_=ast_author_mpb" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button>Shop this book on Amazon</Button>
          </a>

        </BuyContainer>

        <TropesContainer>

          <h1>➼ Forbidden love</h1>
          <h1>➼ One bed... under the stars</h1>
          <h1>➼ Nail-biting suspense</h1>
          <h1>➼ Protective MMC</h1>
          <h1>➼ She's an artist/ He's a climber</h1>

        </TropesContainer>

      </PromoContainer>

      <FullBox>

        <ReviewContainer>

          <Title>What Readers Say</Title>

          <CarouselContainer>

            <Arrow onClick={handlePrev}>❮</Arrow>

            <QuoteBox>
              
              <Paragraph style={{fontWeight: "bold"}}>
                {quotes[currentQuote].author} <span style={{color: "#caaeecff", marginLeft: "10px"}}>★★★★★</span>
              </Paragraph> <br/>

              <Paragraph style={{fontStyle: "italic"}}>“{quotes[currentQuote].text}”</Paragraph>

            </QuoteBox>

            <Arrow onClick={handleNext}>❯</Arrow>
          
          </CarouselContainer>

          <a 
            href="https://www.goodreads.com/book/show/220376461-beyond-prejudice" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button>See all reviews on Goodreads</Button>
          </a>

        </ReviewContainer>

      </FullBox>

      <ConnectContainer>

        <LinksContainer>

          <Link href="https://www.instagram.com/annamiki_author/" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </Link>

          <Link href="https://www.tiktok.com/@annamiki84" target="_blank" rel="noopener noreferrer">
            <FaTiktok />
          </Link>

          <Link href="https://www.goodreads.com/author/show/51714605.Anna_Miki" target="_blank" rel="noopener noreferrer">
            <FaGoodreads />
          </Link>

          <Link href="https://www.amazon.com/stores/Anna-Miki/author/B0DKYMFFFN" target="_blank" rel="noopener noreferrer">
            <FaAmazon />
          </Link>

        </LinksContainer>

        <Newsletter/>

      </ConnectContainer>

      <Footer />

    </Parallax>
  );
};

export default BeyondPrejudice;
